"use client";
import Link from "next/link";
import React from "react";
import { useRouter } from "next/navigation";
import { SectionHeading } from "./SectionHeading";
import { WarningNote } from "./WarningNote";
import { InfoNote } from "./InfoNote";
import { PullQuote } from "./PullQuote";
import { ComparisonTable } from "./ComparisonTable";
import { KeyStatBlock } from "./KeyStatBlock";

type ContentRendererProps = {
  content: string;
};

type Segment =
  | { kind: "html"; html: string }
  | { kind: "section-heading"; id: string; children: string }
  | { kind: "pullquote"; children: string }
  | { kind: "key-stat-block"; label: string; value: string; children: string }
  | { kind: "warning-note"; label?: string; children: string }
  | { kind: "info-note"; label?: string; children: string }
  | { kind: "comparison-table"; columns: { key: string; label: string }[]; rows: Record<string, string>[] }
  | { kind: "cta-banner"; title: string; description: string; href: string; buttonText: string }
  | { kind: "at-a-glance"; title: string; items: string[] }
  | { kind: "tldr-card"; html: string };

function parseAttrs(attrString: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  if (!attrString) return attrs;
  const parts = attrString.split("|");
  for (const part of parts) {
    const eqIdx = part.indexOf("=");
    if (eqIdx !== -1) {
      const key = part.slice(0, eqIdx).trim();
      let val = part.slice(eqIdx + 1).trim();
      if ((val.startsWith('"') && val.endsWith('"')) || (val.startsWith("'") && val.endsWith("'"))) {
        val = val.slice(1, -1).trim();
      }
      attrs[key] = val;
    }
  }
  return attrs;
}

function parseContent(html: string): Segment[] {
  const segments: Segment[] = [];

  const patterns: {
    regex: RegExp;
    parse: (m: RegExpExecArray) => Segment;
  }[] = [
    {
      regex: /<!--COMPONENT:pullquote-->([\s\S]*?)<!--ENDOFCOMPONENT-->/g,
      parse: (m) => ({ kind: "pullquote", children: m[1].trim() }),
    },
    {
      regex: /<!--COMPONENT:key-stat-block(?:\|([\s\S]*?))?-->([\s\S]*?)<!--ENDOFCOMPONENT-->/g,
      parse: (m) => {
        const attrs = parseAttrs(m[1]);
        return {
          kind: "key-stat-block",
          label: attrs.label || "",
          value: attrs.value || "",
          children: m[2].trim(),
        };
      },
    },
    {
      regex: /<!--COMPONENT:warning-note(?:\|([\s\S]*?))?-->([\s\S]*?)<!--ENDOFCOMPONENT-->/g,
      parse: (m) => {
        const attrs = parseAttrs(m[1]);
        return {
          kind: "warning-note",
          label: attrs.label,
          children: m[2].trim(),
        };
      },
    },
    {
      regex: /<!--COMPONENT:info-note(?:\|([\s\S]*?))?-->([\s\S]*?)<!--ENDOFCOMPONENT-->/g,
      parse: (m) => {
        const attrs = parseAttrs(m[1]);
        return {
          kind: "info-note",
          label: attrs.label,
          children: m[2].trim(),
        };
      },
    },
    {
      regex: /<!--COMPONENT:comparison-table\|json=([\s\S]*?)-->[\s\S]*?<!--ENDOFCOMPONENT-->/g,
      parse: (m) => {
        try {
          const parsed = JSON.parse(m[1].trim());
          return {
            kind: "comparison-table",
            columns: parsed.columns || [],
            rows: parsed.rows || [],
          };
        } catch (e) {
          if (process.env.NODE_ENV !== "production") console.error("Failed to parse comparison-table JSON:", e);
          return { kind: "comparison-table", columns: [], rows: [] };
        }
      },
    },
    {
      regex: /<div[^>]*?class="[^"]*?my-8 p-6 rounded-xl border border-border-custom bg-surface flex flex-col sm:flex-row items-center gap-6 justify-between shadow-card[^"]*"[^>]*?>\s*<div[^>]*?class="flex-1"[^>]*?>\s*<h3[^>]*?>([\s\S]*?)<\/h3>\s*<p[^>]*?>([\s\S]*?)<\/p>\s*<\/div>\s*<a[^>]*?href="([^"]*)"[^>]*?>([\s\S]*?)<\/a>\s*<\/div>/gi,
      parse: (m) => ({
        kind: "cta-banner",
        title: m[1].trim(),
        description: m[2].trim(),
        href: m[3].trim(),
        buttonText: m[4].trim(),
      }),
    },
    {
      regex: /<div[^>]*?class="[^"]*?(?:bg-accent\/5[^"]*?border-accent\/20|border-accent\/20[^"]*?bg-accent\/5)[^"]*"[^>]*?>\s*<h3[^>]*?>([\s\S]*?)<\/h3>\s*<ul[^>]*?>([\s\S]*?)<\/ul>\s*<\/div>/gi,
      parse: (m) => {
        const h3Text = m[1].trim();
        const ulContent = m[2];
        const liMatches = ulContent.match(/<li[^>]*?>([\s\S]*?)<\/li>/gi) || [];
        const lis = liMatches.map(li => {
          const match = /<li[^>]*?>([\s\S]*?)<\/li>/i.exec(li);
          return match ? match[1].trim() : "";
        });
        return {
          kind: "at-a-glance",
          title: h3Text,
          items: lis,
        };
      }
    },
    {
      regex: /<div[^>]*?class="[^"]*?my-6 p-4 rounded-lg bg-surface border border-border-custom[^"]*"[^>]*?>\s*<p[^>]*?>The Short Version<\/p>\s*([\s\S]*?)<\/div>/gi,
      parse: (m) => ({
        kind: "tldr-card",
        html: m[1].trim(),
      }),
    },
    {
      regex: /<div[^>]*?class="[^"]*?my-6 p-4 rounded-lg bg-amber-500\/10 border border-amber-500\/30[^"]*"[^>]*?>\s*<p[^>]*?>([\s\S]*?)<\/p>\s*([\s\S]*?)<\/div>/gi,
      parse: (m) => {
        const label = m[1].trim();
        let content = m[2].trim();
        const pMatch = /<p[^>]*?>([\s\S]*?)<\/p>/i.exec(content);
        if (pMatch) {
          content = pMatch[1].trim();
        }
        return {
          kind: "info-note",
          label: label,
          children: content,
        };
      },
    },
    {
      regex: /<h3\s+id="([^"]*)"\s*>(.*?)<\/h3>/g,
      parse: (m) => ({ kind: "section-heading", id: m[1], children: m[2] }),
    },
  ];

  interface MatchInfo {
    index: number;
    endIndex: number;
    segment: Segment;
  }

  const allMatches: MatchInfo[] = [];

  for (const pattern of patterns) {
    pattern.regex.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = pattern.regex.exec(html)) !== null) {
      allMatches.push({
        index: m.index,
        endIndex: pattern.regex.lastIndex,
        segment: pattern.parse(m),
      });
    }
  }

  allMatches.sort((a, b) => a.index - b.index);

  const filteredMatches: MatchInfo[] = [];
  for (const match of allMatches) {
    if (filteredMatches.length === 0 || match.index >= filteredMatches[filteredMatches.length - 1].endIndex) {
      filteredMatches.push(match);
    }
  }

  let cursor = 0;
  for (const match of filteredMatches) {
    if (match.index > cursor) {
      segments.push({ kind: "html", html: html.slice(cursor, match.index) });
    }
    segments.push(match.segment);
    cursor = match.endIndex;
  }

  if (cursor < html.length) {
    segments.push({ kind: "html", html: html.slice(cursor) });
  }

  const renderMarkdownInHtml = (h: string) => {
    return h
      .replace(/\*\*([\s\S]*?)\*\*/g, "<strong>$1</strong>")
      .replace(/<img\s(?!([^>]*alt=))/gi, '<img alt="Article image" ')
      .replace(/<img\s(?![^>]*loading=)/gi, '<img loading="lazy" width="800" height="400" ');
  };

  return segments.map(seg => {
    if (seg.kind === "html") {
      return { ...seg, html: renderMarkdownInHtml(seg.html) };
    }
    if (seg.kind === "pullquote") {
      return { ...seg, children: renderMarkdownInHtml(seg.children) };
    }
    if (seg.kind === "key-stat-block") {
      return { ...seg, children: renderMarkdownInHtml(seg.children) };
    }
    if (seg.kind === "warning-note") {
      return { ...seg, children: renderMarkdownInHtml(seg.children) };
    }
    if (seg.kind === "info-note") {
      return { ...seg, children: renderMarkdownInHtml(seg.children) };
    }
    if (seg.kind === "at-a-glance") {
      return { ...seg, items: seg.items.map(item => renderMarkdownInHtml(item)) };
    }
    if (seg.kind === "tldr-card") {
      return { ...seg, html: renderMarkdownInHtml(seg.html) };
    }
    return seg;
  });
}

export function ContentRenderer({ content }: ContentRendererProps) {
  const router = useRouter();
  const segments = parseContent(content);
  const elements: React.ReactNode[] = [];

  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest("a");
    if (anchor) {
      const href = anchor.getAttribute("href");
      if (href && href.startsWith("/") && !href.startsWith("//")) {
        e.preventDefault();
        router.push(href);
      }
    }
  };

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];

    switch (seg.kind) {
      case "html":
        elements.push(
          <div key={`html-${i}`} dangerouslySetInnerHTML={{ __html: seg.html }} className="text-left" />
        );
        break;
      case "section-heading":
        elements.push(
          <SectionHeading key={`sh-${i}`} id={seg.id}>
            {seg.children}
          </SectionHeading>
        );
        break;
      case "pullquote":
        elements.push(
          <PullQuote key={`pq-${i}`}>
            <span dangerouslySetInnerHTML={{ __html: seg.children }} />
          </PullQuote>
        );
        break;
      case "key-stat-block":
        elements.push(
          <KeyStatBlock key={`ksb-${i}`} label={seg.label} value={seg.value}>
            <span dangerouslySetInnerHTML={{ __html: seg.children }} />
          </KeyStatBlock>
        );
        break;
      case "warning-note":
        elements.push(
          <WarningNote key={`wn-${i}`} label={seg.label}>
            <span dangerouslySetInnerHTML={{ __html: seg.children }} />
          </WarningNote>
        );
        break;
      case "info-note":
        elements.push(
          <InfoNote key={`in-${i}`} label={seg.label}>
            <span dangerouslySetInnerHTML={{ __html: seg.children }} />
          </InfoNote>
        );
        break;
      case "comparison-table":
        elements.push(
          <ComparisonTable key={`ct-${i}`} columns={seg.columns} rows={seg.rows} />
        );
        break;
      case "cta-banner":
        elements.push(
          <div key={`cta-${i}`} className="my-12 p-6 rounded-lg border border-[var(--line)] bg-[var(--surface-muted)] flex flex-col sm:flex-row items-center gap-6 justify-between text-left card-blueprint">
            <div className="flex-1">
              <h3 className="text-xl font-bold text-[var(--ink)] font-heading-condensed m-0 mb-2">{seg.title}</h3>
              <p className="text-sm text-[var(--ink-soft)] m-0 font-sans">{seg.description}</p>
            </div>
            <Link href={seg.href} className="flex items-center justify-center px-6 py-3 bg-[var(--accent)] hover:bg-[var(--accent-dark)] text-white rounded font-mono font-bold text-xs uppercase tracking-wider transition-colors shrink-0 text-center whitespace-nowrap w-full sm:w-auto min-w-[180px]">
              {seg.buttonText}
            </Link>
          </div>
        );
        break;
      case "at-a-glance":
        elements.push(
          <div key={`aag-${i}`} className="my-6 pl-5 border-l-2 border-[var(--accent)] bg-transparent w-full text-left">
            <p className="text-[10px] uppercase tracking-wider text-[var(--accent)] font-mono font-bold mb-3 block">
              {seg.title}
            </p>
            <ul className="space-y-2 text-[var(--ink-soft)] text-base font-sans">
              {seg.items.map((item: string, idx: number) => (
                <li key={idx} className="flex items-start">
                  <span className="text-[var(--accent)] mr-2 select-none">›</span>
                  <span dangerouslySetInnerHTML={{ __html: item }} />
                </li>
              ))}
            </ul>
          </div>
        );
        break;
      case "tldr-card":
        elements.push(
          <div key={`tldr-${i}`} className="my-8 text-left relative card-tldr">
            {/* Header bar */}
            <div className="pl-5 pr-4 py-2.5 bg-[var(--surface-muted)] border-b border-[var(--border)] rounded-t flex items-center gap-2">
              <svg className="w-3.5 h-3.5 text-[var(--rust)] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-[10px] font-mono font-bold tracking-widest text-[var(--ink)] uppercase select-none">
                Summary
              </span>
            </div>
            
            {/* Content area */}
            <div className="tldr-card-content pl-5 pr-5 py-4 text-sm text-[var(--ink-soft)] leading-relaxed font-sans font-medium space-y-3">
              <div dangerouslySetInnerHTML={{ __html: seg.html }} />
            </div>
          </div>
        );
        break;
    }
  }

  return (
    <div onClick={handleContentClick} className="w-full flex flex-col">
      {elements}
    </div>
  );
}
